/**
 * Version WebSocket client: shared envelope for pipeline / LLM / agent.
 */

export type LiveEventType =
  | "hello"
  | "pipeline_step"
  | "llm_delta"
  | "llm_usage"
  | "llm_done"
  | "agent_delta"
  | "agent_tool"
  | "agent_tool_end"
  | "agent_done"
  | "agent_error"
  | "error";

export type LiveEvent = {
  type: LiveEventType;
  seq: number;
  epoch: string;
  run_id: string | null;
  call_id: string | null;
  step_id: string | null;
  payload: Record<string, unknown>;
};

export type LiveClientOptions = {
  versionId: string;
  onEvent: (event: LiveEvent) => void;
  onStatus?: (status: "connecting" | "open" | "closed" | "error") => void;
};

export class VersionLiveClient {
  private ws: WebSocket | null = null;
  private versionId: string;
  private onEvent: (event: LiveEvent) => void;
  private onStatus?: LiveClientOptions["onStatus"];
  private lastSeq = 0;
  private epoch = "";
  private closed = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: LiveClientOptions) {
    this.versionId = opts.versionId;
    this.onEvent = opts.onEvent;
    this.onStatus = opts.onStatus;
  }

  connect(): void {
    this.closed = false;
    this.open();
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  subscribeCall(callId: string): void {
    this.send({ type: "subscribe_call", call_id: callId });
  }

  sendAgentPrompt(text: string): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.send({ type: "agent_prompt", text });
    return true;
  }

  cancelAgent(): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.send({ type: "agent_cancel" });
    return true;
  }

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private open(): void {
    if (this.closed) return;
    this.onStatus?.("connecting");
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const params = new URLSearchParams();
    if (this.lastSeq > 0) params.set("after_seq", String(this.lastSeq));
    if (this.epoch) params.set("epoch", this.epoch);
    const qs = params.toString();
    const url = `${proto}//${window.location.host}/api/ws/versions/${encodeURIComponent(this.versionId)}${qs ? `?${qs}` : ""}`;

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.onStatus?.("open");
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data !== "string") return;
      let event: LiveEvent;
      try {
        event = JSON.parse(ev.data) as LiveEvent;
      } catch {
        return;
      }
      if (event.type === "hello") {
        this.epoch = event.epoch;
        const last = event.payload?.last_seq;
        if (typeof last === "number" && last > this.lastSeq) {
          // server last_seq is informational; do not skip live seqs
        }
      } else if (typeof event.seq === "number" && event.seq > 0) {
        this.lastSeq = Math.max(this.lastSeq, event.seq);
        if (event.epoch) this.epoch = event.epoch;
      }
      this.onEvent(event);
    };

    ws.onerror = () => {
      this.onStatus?.("error");
    };

    ws.onclose = () => {
      this.onStatus?.("closed");
      this.ws = null;
      if (!this.closed) {
        this.reconnectTimer = setTimeout(() => this.open(), 1200);
      }
    };
  }

  private send(frame: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
    }
  }
}
